import React, { useMemo, useRef, useState } from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import styles from './TelehealthBilling.module.scss';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { Trans, useTranslation, UseTranslationResponse } from 'react-i18next';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import formStyles from '../../../components/form/form.module.scss';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { TelehealthBillingQuery } from 'services/api/api-client.ts';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { MenuItem, Select } from '@mui/material';
import {
  abasUnavailableUrn,
  ErrObj,
  formatDate,
} from '../../../helpers/abas-helper.ts';
import { sub } from 'date-fns';
import { downloadOrOpenBlobFile } from '../../../helpers/download-helper.ts';
import { LottieContainer } from '../../../components/animations/LottieContainer.tsx';
import animationData from '../../../assets/animations/loader_small.json';
import { useProcessLinks } from '../../../helpers/useProcessLinks.ts';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { TrackEventParams } from '@jonkoops/matomo-tracker-react/lib/types';
import { Tip } from '../../../components/uikit/Tip.tsx';
import { tDynamic } from '../../../helpers/tDynamic.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

export const TelehealthBillingCategory = 'TelehealthBilling';

type Form = {
  invoice: number | null;
  amountOfPeriods: number | null;
  period: string;
};

const pageSizes = [10, 20, 50];

const mail = 'supportHC@loewensteinmedical.com';
const none = 'none';

export const TelehealthBilling = () => {
  const i18n = useScopedTranslation('Page.TelehealthBilling');
  const i18nCommon = useTranslation();

  const { trackEvent } = useAnalytics();

  const [isDateSelectVisible, setIsDateSelectVisible] = useState(false);

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    isTooltipVisible,
    closeTooltip,
  } = useProcessLinks();

  const periodOptions = ['year', 'month', 'week', 'day'];

  const [params, setParams] = useQueryParams({
    invoice: NumberParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'date'),
    desc: withDefault(BooleanParam, true),
    amountOfPeriods: NumberParam,
    period: withDefault(StringParam, periodOptions[1], true),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({ ...params, ...data, page: 1 });
    },
    {
      defaultValues: {
        invoice: params.invoice ?? null,
        amountOfPeriods: params.amountOfPeriods ?? 1,
        period: params.period ?? periodOptions[1],
      },
    },
  );

  const [invoice, amountOfPeriods, period] = useWatch({
    control: form.control,
    name: ['invoice', 'amountOfPeriods', 'period'],
  });

  const getOrderedFrom = useMemo(() => {
    if (period && amountOfPeriods) {
      return sub(new Date(), {
        [period + 's']: amountOfPeriods,
      });
    } else {
      return null;
    }
  }, [params.period, params.amountOfPeriods]);

  const telehealthBilling = TelehealthBillingQuery.useGetQuery(
    {
      invoiceNumber: String(params.invoice ?? ''),
      date: getOrderedFrom,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const columnsConfig = getColumnsConfig(
    i18n,
    i18nCommon,
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    trackEvent,
  );
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const numberOfRows = telehealthBilling.data?.documents.length ?? 0;

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: telehealthBilling.data?.countOfRecords ?? 0,
    pageSizes: pageSizes,
    numberOfRows: numberOfRows,
    changePagination: changePagination,
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const resetFilters = () => {
    form.reset({
      invoice: null,
      amountOfPeriods: 1,
      period: periodOptions[1],
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShown =
    !!invoice || amountOfPeriods !== 1 || period !== periodOptions[1];

  const errObj = telehealthBilling?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !numberOfRows &&
    !telehealthBilling.isLoading &&
    !telehealthBilling.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'telehealth-billing-container'}>
      <Breadcrumbs />
      <Loading
        loading={
          telehealthBilling.isLoading || telehealthBilling.isPlaceholderData
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.filterRowContainer}>
            <Input
              testId={'invoice-input'}
              {...form.register('invoice')}
              leftHelperText={i18n.t('invoice')}
              className={styles.width184}
              type={'number'}
              endAdornment={
                <div>
                  {invoice && (
                    <IconButton
                      type={'destroy'}
                      onClick={() => form.setValue('invoice', null)}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.endAdornmentClassname}
            />
            <div
              className={clsx(formStyles.rowContainer, styles.alignItemsUnset)}
            >
              <Input
                testId={'time-interval-input'}
                className={clsx(styles.width92)}
                leftHelperText={i18n.t('dates_for_last')}
                {...form.register('amountOfPeriods')}
                type={'number'}
                endAdornment={
                  <div>
                    {amountOfPeriods && (
                      <IconButton
                        type={'destroy'}
                        onClick={() => form.setValue('amountOfPeriods', null)}
                      >
                        <Cross />
                      </IconButton>
                    )}
                  </div>
                }
                endAdornmentClassname={styles.endAdornmentClassname}
              />
              <Select
                data-test-id={'time-period-select'}
                open={isDateSelectVisible}
                onClick={() => {
                  setIsDateSelectVisible(true);
                }}
                onClose={(e) => {
                  e.stopPropagation();
                  setIsDateSelectVisible(false);
                }}
                value={period}
                onChange={(e) => form.setValue('period', e.target.value)}
                className={clsx(formStyles.select, styles.width120)}
                IconComponent={ArrowDown}
                MenuProps={{
                  PaperProps: {
                    className: clsx(formStyles.selectBody, styles.width120),
                  },
                  disableScrollLock: true,
                }}
              >
                {periodOptions.map((periodOption) => (
                  <MenuItem key={periodOption} value={periodOption}>
                    {tDynamic(i18n, periodOption)}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <Button
              type={'submit'}
              className={clsx(styles.actionButton)}
              title={i18n.t('apply')}
              testId={'filters-apply-button'}
            />
            {isResetButtonShown && (
              <NegativeActionText
                testId={'filters-reset-button'}
                text={i18n.t('reset')}
                onClickHandler={resetFilters}
              />
            )}
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={telehealthBilling.data?.documents}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: 150 }}
            noDataText={''}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x, index) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={`${x.dataField}.${index}`}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                      isSortingBlocked={index === columnsConfig.length - 1}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        {isPlaceholderVisible && <TablePlaceholder testId={'placeholder'} />}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {!!numberOfRows && <AppPagination {...paginationProps} />}
        {isTooltipVisible && (
          <Tooltip
            isError={true}
            closeTooltip={closeTooltip}
            bodyContent={
              <Trans
                i18nKey={'fileNotFound'}
                components={{
                  supportLink: <a href={`mailto:${mail}`} />,
                }}
              />
            }
            className={styles.tooltip}
            showCancelButton={true}
            iconClassName={styles.tooltipIcon}
          />
        )}
      </Loading>
    </div>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.TelehealthBilling'>,
  i18nCommon: UseTranslationResponse<'translation', undefined>,
  addProcessingLink: (link: string) => void,
  removeProcessingLink: (link: string) => void,
  processingLinks: Set<string>,
  showTooltip: () => void,
  trackEvent: (params: TrackEventParams) => void | undefined,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'invoice',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('invoice'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 200,
  },
  {
    dataField: 'position',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('position'),
    cssClass: clsx(
      formStyles.textCellPadding,
      styles.textAlignLeft,
      formStyles.tableHeaderPointer,
    ),
    minWidth: 200,
  },
  {
    dataField: 'date',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('date'),
    cssClass: clsx(
      formStyles.textCellPadding,
      styles.textAlignLeft,
      formStyles.tableHeaderPointer,
    ),
    cellRender: (cell) => formatDate(cell.value),
    minWidth: 200,
  },
  {
    dataField: 'link',
    allowEditing: false,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 136,
    cellRender: (cell) => {
      const filePath = cell.value;
      const extension = filePath.slice(filePath.lastIndexOf('.') + 1);

      const fileName = cell.data.invoice + '—' + cell.data.position;

      const link = cell.value;
      const isLoading = processingLinks.has(link);

      if (cell.value === none) {
        return (
          <Tip
            tooltipText={i18nCommon.t('link_unavailable_msg')}
            textBody={
              <div className={styles.disabledButton}>{i18n.t('download')}</div>
            }
            className={styles.tip}
            tooltipClassName={styles.margins}
          />
        );
      }

      return (
        <>
          {isLoading ? (
            <LottieContainer
              animationData={animationData}
              segments={isLoading ? [1, 180] : [180, 270]}
              loop={isLoading}
              width={28}
              height={28}
            />
          ) : (
            <div>
              <Button
                testId={'download-button'}
                className={clsx(styles.actionButton)}
                title={i18n.t('download')}
                onClick={() => {
                  trackEvent({
                    category: TelehealthBillingCategory,
                    action: 'Download file',
                    name: link,
                  });
                  addProcessingLink(link);
                  downloadOrOpenBlobFile(filePath, fileName)
                    .catch(showTooltip)
                    .finally(() => removeProcessingLink(link));
                }}
              />
              <div className={styles.greyText}>{extension}</div>
            </div>
          )}
        </>
      );
    },
  },
];
